import { ReactNode, useEffect } from "react";
import { motion } from "framer-motion";
import { LucideIcon } from "lucide-react";

export interface MarketingHeroProps {
  mobileImage: string;   // pass .webp for best results
  desktopImage: string;  // pass .webp for best results
  badge?: {
    icon?: LucideIcon;
    text: string;
  };
  title: ReactNode;
  subtitle?: ReactNode;
  disclaimer?: string;
  children?: ReactNode;
  testId?: string;
  priority?: boolean;
}

function toJpgFallback(url: string) {
  // If you pass .webp, we fall back to .jpg (which you already have)
  return url.replace(/\.(webp|avif)$/i, ".jpg");
}

export default function MarketingHero({
  mobileImage,
  desktopImage,
  badge,
  title,
  subtitle,
  disclaimer,
  children,
  testId,
  priority = false,
}: MarketingHeroProps) {
  useEffect(() => {
    if (!priority) return;

    const mobileId = `preload-hero-mobile-${mobileImage.replace(/[^a-z0-9]/gi, "_")}`;
    const desktopId = `preload-hero-desktop-${desktopImage.replace(/[^a-z0-9]/gi, "_")}`;

    let mobilePreload = document.getElementById(mobileId) as HTMLLinkElement | null;
    let desktopPreload = document.getElementById(desktopId) as HTMLLinkElement | null;

    if (!mobilePreload) {
      mobilePreload = document.createElement("link");
      mobilePreload.id = mobileId;
      mobilePreload.rel = "preload";
      mobilePreload.as = "image";
      mobilePreload.href = mobileImage;
      mobilePreload.media = "(max-width: 768px)";
      mobilePreload.setAttribute("fetchpriority", "high");
      document.head.appendChild(mobilePreload);
    }

    if (!desktopPreload) {
      desktopPreload = document.createElement("link");
      desktopPreload.id = desktopId;
      desktopPreload.rel = "preload";
      desktopPreload.as = "image";
      desktopPreload.href = desktopImage;
      desktopPreload.media = "(min-width: 769px)";
      desktopPreload.setAttribute("fetchpriority", "high");
      document.head.appendChild(desktopPreload);
    }
  }, [priority, mobileImage, desktopImage]);

  const loading = priority ? "eager" : "lazy";
  const fetchPriority = priority ? ("high" as const) : undefined;

  return (
    <section className="relative w-full min-h-[70vh] sm:min-h-[72vh] lg:min-h-[76vh] max-h-[820px] flex items-center">
      {/* Mobile */}
      <picture className="md:hidden">
        <source type="image/webp" srcSet={mobileImage} />
        <img
          src={toJpgFallback(mobileImage)}
          alt=""
          width={750}
          height={1000}
          loading={loading}
          fetchPriority={fetchPriority}
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover object-top md:hidden"
        />
      </picture>

      {/* Desktop */}
      <picture className="hidden md:block">
        <source type="image/webp" srcSet={desktopImage} />
        <img
          src={toJpgFallback(desktopImage)}
          alt=""
          width={1920}
          height={1080}
          loading={loading}
          fetchPriority={fetchPriority}
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover object-top hidden md:block"
        />
      </picture>

      <div className="absolute inset-0 bg-slate-950/60" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />

      <div className="relative z-10 mx-auto max-w-4xl text-center px-6">
        {badge && (
          <motion.div
            initial={{ opacity: 1, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/10 text-teal-400 text-xs font-medium mb-6 uppercase tracking-wider"
          >
            {badge.icon && <badge.icon size={12} />}
            {badge.text}
          </motion.div>
        )}

        {/* H1 is the LCP element, render it visible immediately (no opacity gate)
            so LCP is not delayed until hydration. Surrounding elements still animate. */}
        <h1
          className="text-4xl md:text-5xl font-bold tracking-tight mb-6 text-white"
          data-testid={testId}
        >
          {title}
        </h1>

        {subtitle && (
          <motion.p
            initial={{ opacity: 1, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed"
          >
            {subtitle}
          </motion.p>
        )}

        {disclaimer && (
          <motion.p
            initial={{ opacity: 1, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="text-sm text-slate-500 max-w-xl mx-auto mb-8"
          >
            {disclaimer}
          </motion.p>
        )}

        {children}
      </div>
    </section>
  );
}